/**
 * Chunk U5 — Incomplete upload listing, resume filters, and 7-day cleanup.
 * Sparse OCR uploads stay in Incomplete until the user confirms or removes them.
 *
 * Confirmed / reconciled / archived invoices are never discarded.
 * Server discard-upload is the safety net; client mirrors eligibility.
 */

import {
  UPLOAD_DRAFT_STATUS,
  UPLOAD_QUEUE_COPY,
  UPLOAD_QUEUE_INCOMPLETE_DISCARD_DAYS,
  filterExpiredIncompleteUploads,
  isIncompleteUploadExpired,
} from './invoiceUploadQueueHelpers'
import {
  INCOMPLETE_UPLOAD_KIND,
  buildIncompleteEntryFromSession,
  getIncompleteUploadInvoiceIds,
  loadIncompleteUploads,
  persistIncompleteSession,
  pruneExpiredIncompleteUploads,
  rebuildUploadSessionFromIncomplete,
  removeIncompleteUploads,
  saveIncompleteUploads,
  upsertIncompleteUploads,
} from './invoiceUploadIncompleteStorage'
import {
  isSparseLibraryIncompleteInvoice,
  mergeSameInvoiceSessionPages,
} from './invoiceUploadSameInvoicePages'

function invoiceIdOf(invoice = {}) {
  return String(invoice._id || invoice.id || invoice.invoiceId || '').trim()
}

function hasNonEmptyText(value) {
  return Boolean(String(value || '').trim())
}

/**
 * Mirrors express discard-upload confirmation guards.
 * True when discard-upload must NOT run.
 *
 * OCR auto-enrich writes invoice number / supplier / total before Confirm.
 * `uploadConfirmed: false` keeps those drafts discardable. Missing flag
 * (legacy invoices) still treats populated fields as confirmed.
 */
export function isInvoiceProtectedFromIncompleteDiscard(invoice = {}) {
  if (!invoice) return true
  if (invoice.isArchived) return true
  const status = String(invoice.status || 'pending').trim()
  if (status && status !== 'pending') return true
  if (invoice.uploadConfirmed === true) return true
  if (invoice.uploadConfirmed === false) return false
  if (
    invoice.supplier ||
    hasNonEmptyText(invoice.supplierTitle) ||
    hasNonEmptyText(invoice.purchaseOrderNumber) ||
    invoice.linkedOrderId ||
    hasNonEmptyText(invoice.invoiceNumber) ||
    invoice.invoiceDate ||
    invoice.invoiceTotal != null
  ) {
    return true
  }
  return false
}

/** Sparse pending upload still waiting for confirm (library-side incomplete). */
export function isLibraryInvoiceIncompleteUpload(invoice = {}) {
  if (!invoice) return false
  if (isInvoiceProtectedFromIncompleteDiscard(invoice)) return false
  return true
}

/**
 * Blank OCR orphans: pending library rows with almost no usable fields.
 * Listed for user review — never auto-discarded.
 */
export function isSparseOrphanLibraryIncomplete(invoice = {}) {
  if (!isLibraryInvoiceIncompleteUpload(invoice)) return false
  return isSparseLibraryIncompleteInvoice(invoice)
}

/**
 * Invoice ids to exclude from matching-work badge (tray + library incompletes).
 */
export function getMatchingWorkExcludeInvoiceIds(
  venueId,
  libraryInvoices = [],
) {
  const exclude = new Set(
    [...getIncompleteUploadInvoiceIds(venueId)].map(id => String(id)),
  )
  for (const inv of libraryInvoices || []) {
    if (!isLibraryInvoiceIncompleteUpload(inv)) continue
    const id = invoiceIdOf(inv)
    if (id) exclude.add(id)
  }
  return exclude
}

export function getLibraryInvoiceCreatedAt(invoice = {}) {
  const raw =
    invoice.uploadedDate ||
    invoice.createdAt ||
    invoice.created_at ||
    invoice.dateUploaded ||
    null
  if (!raw) return null
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

export function buildIncompleteEntryFromLibraryInvoice(invoice = {}) {
  const invoiceId = invoiceIdOf(invoice)
  if (!invoiceId) return null
  if (!isLibraryInvoiceIncompleteUpload(invoice)) return null

  const suggested = invoice.ocr?.suggested || null
  return {
    draftId: `lib-${invoiceId}`,
    invoiceId,
    pickIndex: 0,
    filename:
      invoice.originalFilename ||
      invoice.filename ||
      invoice.fileName ||
      'Upload',
    status: UPLOAD_DRAFT_STATUS.READY,
    needsReview: true,
    incompleteKind: INCOMPLETE_UPLOAD_KIND.REVIEW_LATER,
    documentType: invoice.documentType || 'invoice',
    supplier: null,
    supplierTitle: '',
    purchaseOrderNumber: '',
    linkedOrderId: null,
    invoiceNumber: '',
    invoiceDate: null,
    invoiceTotalInput: '',
    invoiceTotalSource: null,
    comment: '',
    ocrStatus: invoice.ocr?.status || (suggested ? 'suggested' : 'idle'),
    ocrSuggested: suggested,
    ocrWarnings: invoice.ocr?.warnings || [],
    ocrError: invoice.ocr?.error || null,
    createdAt: getLibraryInvoiceCreatedAt(invoice) || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

/**
 * Merge localStorage tray + library incomplete drafts (API-less list).
 * Drops protected / confirmed / archived / missing-from-library when known.
 */
export function listIncompleteUploadsForVenue({
  venueId,
  libraryInvoices = [],
  now = new Date(),
} = {}) {
  const library = Array.isArray(libraryInvoices) ? libraryInvoices : []
  const libraryById = new Map(
    library.map(inv => [invoiceIdOf(inv), inv]).filter(([id]) => id),
  )

  const fromStorage = loadIncompleteUploads(venueId)
  const keptFromStorage = []

  for (const entry of fromStorage) {
    const id = String(entry.invoiceId || '')
    const lib = libraryById.get(id)
    if (lib) {
      if (isInvoiceProtectedFromIncompleteDiscard(lib)) {
        // Confirmed / reconciled / archived — drop from tray
        continue
      }
      keptFromStorage.push({
        ...entry,
        createdAt: entry.createdAt || getLibraryInvoiceCreatedAt(lib),
        needsReview: true,
      })
      continue
    }
    // Library not loaded or invoice gone — keep storage row until cleanup
    // proves expiry / discard (unless we know library was provided empty scan).
    if (library.length === 0) {
      keptFromStorage.push(entry)
    }
    // If library loaded and id missing → draft already discarded/deleted
  }

  const storageIds = new Set(keptFromStorage.map(e => String(e.invoiceId)))
  const fromLibrary = []
  for (const inv of library) {
    const id = invoiceIdOf(inv)
    if (!id || storageIds.has(id)) continue
    if (!isLibraryInvoiceIncompleteUpload(inv)) continue
    const entry = buildIncompleteEntryFromLibraryInvoice(inv)
    if (entry) {
      entry.pickIndex = keptFromStorage.length + fromLibrary.length
      fromLibrary.push(entry)
    }
  }

  const merged = [...keptFromStorage, ...fromLibrary].sort(
    (a, b) => Number(a.pickIndex || 0) - Number(b.pickIndex || 0),
  )

  // Persist reconciled storage (library discoveries included so tray survives refresh)
  if (venueId) {
    saveIncompleteUploads(venueId, merged)
  }

  return {
    entries: merged,
    count: merged.length,
    now,
  }
}

/**
 * Resume payload: only incomplete (never confirmed/protected).
 */
export function buildResumeSessionFromIncompleteList({
  venueId,
  libraryInvoices = [],
} = {}) {
  const { entries } = listIncompleteUploadsForVenue({
    venueId,
    libraryInvoices,
  })
  const incompleteOnly = entries.filter(e => {
    const lib = (libraryInvoices || []).find(
      inv => invoiceIdOf(inv) === String(e.invoiceId),
    )
    if (lib && isInvoiceProtectedFromIncompleteDiscard(lib)) return false
    return (
      e.status !== UPLOAD_DRAFT_STATUS.CONFIRMED &&
      e.status !== UPLOAD_DRAFT_STATUS.DISCARDED
    )
  })
  return rebuildUploadSessionFromIncomplete(incompleteOnly, libraryInvoices)
}

/**
 * Age filter for cleanup candidates (storage + library).
 */
export function selectExpiredIncompleteForCleanup({
  venueId,
  libraryInvoices = [],
  now = new Date(),
  discardAfterDays = UPLOAD_QUEUE_INCOMPLETE_DISCARD_DAYS,
} = {}) {
  const { entries } = listIncompleteUploadsForVenue({
    venueId,
    libraryInvoices,
    now,
  })
  const expired = filterExpiredIncompleteUploads(entries, {
    now,
    discardAfterDays,
  })

  // Also catch library incompletes older than window even if createdAt missing in storage
  const expiredIds = new Set(expired.map(e => String(e.invoiceId)))
  for (const inv of libraryInvoices || []) {
    if (!isLibraryInvoiceIncompleteUpload(inv)) continue
    const id = invoiceIdOf(inv)
    if (!id || expiredIds.has(id)) continue
    const createdAt = getLibraryInvoiceCreatedAt(inv)
    if (
      isIncompleteUploadExpired(
        { status: UPLOAD_DRAFT_STATUS.READY, createdAt },
        { now, discardAfterDays },
      )
    ) {
      const entry = buildIncompleteEntryFromLibraryInvoice(inv)
      if (entry) {
        expired.push(entry)
        expiredIds.add(id)
      }
    }
  }

  return expired
}

/**
 * Blank / near-blank pending library uploads (detection only — not auto-discarded).
 */
export function selectSparseOrphanIncompleteForCleanup({
  libraryInvoices = [],
} = {}) {
  const out = []
  for (const inv of libraryInvoices || []) {
    if (!isSparseOrphanLibraryIncomplete(inv)) continue
    const id = invoiceIdOf(inv)
    if (!id) continue
    out.push({
      invoiceId: id,
      draftId: `lib-${id}`,
      status: UPLOAD_DRAFT_STATUS.READY,
      createdAt: getLibraryInvoiceCreatedAt(inv),
      incompleteKind: INCOMPLETE_UPLOAD_KIND.REVIEW_LATER,
    })
  }
  return out
}

/**
 * On-load cleanup: discard expired unconfirmed uploads, prune tray, banner copy.
 * Sparse OCR orphans are kept for user review — not auto-discarded.
 *
 * @param {{
 *   venueId: string,
 *   libraryInvoices?: object[],
 *   discardInvoiceIds?: (ids: string[]) => Promise<void>,
 *   now?: Date,
 * }} options
 */
export async function runIncompleteUploadsOnLoadCleanup({
  venueId,
  libraryInvoices = [],
  discardInvoiceIds,
  now = new Date(),
} = {}) {
  if (!venueId) {
    return {
      removed: [],
      kept: [],
      discardedIds: [],
      banner: '',
      count: 0,
    }
  }

  const expired = selectExpiredIncompleteForCleanup({
    venueId,
    libraryInvoices,
    now,
  })

  const discardableById = new Map()
  for (const entry of expired) {
    const id = String(entry.invoiceId || '')
    if (!id) continue
    const lib = (libraryInvoices || []).find(inv => invoiceIdOf(inv) === id)
    if (lib && isInvoiceProtectedFromIncompleteDiscard(lib)) continue
    discardableById.set(id, entry)
  }
  const discardable = [...discardableById.values()]

  const discardedIds = []
  if (discardable.length && typeof discardInvoiceIds === 'function') {
    const ids = discardable.map(e => String(e.invoiceId))
    try {
      await discardInvoiceIds(ids)
      discardedIds.push(...ids)
    } catch (error) {
      // Best-effort: still prune local expired rows; server protects confirmed.
      console.error('Incomplete upload cleanup discard failed', error)
    }
  } else {
    discardedIds.push(
      ...discardable.map(e => String(e.invoiceId)).filter(Boolean),
    )
  }

  // Always prune expired from local tray (including ones discard skipped)
  const { removed } = pruneExpiredIncompleteUploads(venueId, { now })
  // Also remove successfully discarded ids that might not have createdAt
  if (discardedIds.length) {
    removeIncompleteUploads(venueId, discardedIds)
  }

  // Re-list after cleanup
  const listed = listIncompleteUploadsForVenue({
    venueId,
    libraryInvoices: (libraryInvoices || []).filter(
      inv => !discardedIds.includes(invoiceIdOf(inv)),
    ),
    now,
  })

  const removedCount = Math.max(removed.length, discardedIds.length)
  return {
    removed: removed.length ? removed : expired,
    kept: listed.entries,
    discardedIds,
    banner:
      removedCount > 0
        ? UPLOAD_QUEUE_COPY.incompleteDiscardedBanner(removedCount)
        : '',
    count: listed.count,
  }
}

/**
 * Persist unfinished session drafts to Incomplete.
 * Same-invoice field merge may enrich the richest page; nothing is discarded
 * in the background — sparse OCR stays for the user to fill or remove.
 */
export async function discardSparseAndPersistIncompleteSession({
  venueId,
  drafts = [],
  files = [],
  incompleteKind = INCOMPLETE_UPLOAD_KIND.REVIEW_LATER,
} = {}) {
  const merged = mergeSameInvoiceSessionPages({ drafts, files })

  const keepDrafts = (merged.drafts || []).filter(
    d =>
      d.status !== UPLOAD_DRAFT_STATUS.DISCARDED &&
      d.status !== UPLOAD_DRAFT_STATUS.CONFIRMED,
  )

  const persisted = venueId
    ? persistIncompleteSession({
        venueId,
        drafts: keepDrafts,
        files: merged.files,
        incompleteKind,
      })
    : []

  return {
    ...merged,
    discardInvoiceIds: [],
    keepDrafts,
    persisted,
  }
}

export function getIncompleteCleanupHint(
  days = UPLOAD_QUEUE_INCOMPLETE_DISCARD_DAYS,
) {
  return UPLOAD_QUEUE_COPY.incompleteDiscardDays(days)
}

// Re-export builders used by tests / callers
export {
  buildIncompleteEntryFromSession,
  isIncompleteUploadExpired,
  upsertIncompleteUploads,
}
